import { getCountry } from 'countries-and-timezones';

/**
 * Display formatting for the feed. Every figure the dashboard prints goes
 * through here, so money, dates and countdowns read the same on every card.
 */

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

const SYMBOLS: Record<string, string> = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
};

/** "$", "£", "A$": the symbol the workbooks and emails print, else the code. */
export function currencySymbol(currency: string | null): string {
  const code = currency ?? 'USD';
  return SYMBOLS[code] ?? code;
}

export function money(
  amount: number | null,
  currency: string | null,
  options: { compact?: boolean } = {},
): string {
  if (amount === null) return '—';
  const compact = options.compact && Math.abs(amount) >= 10_000;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency ?? 'USD',
      notation: compact ? 'compact' : 'standard',
      maximumFractionDigits: compact ? 2 : amount < 100 ? 2 : 0,
      minimumFractionDigits: 0,
    }).format(amount);
  } catch {
    // An unknown ISO code: show the number with the code rather than nothing.
    return `${Math.round(amount).toLocaleString('en-GB')} ${currency ?? ''}`.trim();
  }
}

/**
 * A round figure for running text: compact to three significant figures from
 * 1,000 up ("£6.5m", "£276k", "£500k"), whole below it. The deal page's
 * research sections write valuations and targets this way, as a person would.
 */
export function shortMoney(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency ?? 'USD',
      ...(Math.abs(amount) >= 1000
        ? { notation: 'compact', maximumSignificantDigits: 3 }
        : { maximumFractionDigits: 0 }),
    }).format(amount);
  } catch {
    return money(amount, currency);
  }
}

/**
 * One line of a financial statement: from 1,000 up, compact to three
 * significant figures ("US$218k", "US$1.00m"), so a column of them lines up and
 * reads at a glance. A loss takes a true minus ("−US$946k").
 */
export function statementFigure(amount: number, currency: string): string {
  const compact = Math.abs(amount) >= 1000;
  let text: string;
  try {
    text = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      ...(compact
        ? {
            notation: 'compact',
            minimumSignificantDigits: 3,
            maximumSignificantDigits: 3,
          }
        : { maximumFractionDigits: 0 }),
    }).format(amount);
  } catch {
    text = money(amount, currency);
  }
  return text.replace('-', '−');
}

/**
 * A price per share: always to the cent ("US$7.50", not "US$7.5"), and finer
 * where the source states it ("€0.0125").
 */
export function sharePrice(
  amount: number | null,
  currency: string | null,
): string {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency ?? 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount);
  } catch {
    return `${amount} ${currency ?? ''}`.trim();
  }
}

export function count(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('en-GB');
}

export function percent(value: number): string {
  // Never round up onto 100%: €2,498,000 of a €2,500,000 cap is not full.
  if (value >= 99.5 && value < 100) {
    return `${(Math.floor(value * 10) / 10).toFixed(1)}%`;
  }
  if (value >= 10) return `${Math.round(value).toLocaleString('en-GB')}%`;
  return `${value.toFixed(1).replace(/\.0$/, '')}%`;
}

/**
 * The deadline-urgency scale from design/design-tokens.md. `critical` covers
 * everything inside 72 hours; the 24-hour step is a notification, not a colour.
 */
export type Urgency =
  'calm' | 'soon' | 'urgent' | 'critical' | 'closed' | 'none';

export function urgencyOf(deadline: Date | null, now: number): Urgency {
  if (!deadline) return 'none';
  const left = deadline.getTime() - now;
  if (left <= 0) return 'closed';
  if (left <= 72 * HOUR) return 'critical';
  if (left <= 7 * DAY) return 'urgent';
  if (left <= 14 * DAY) return 'soon';
  return 'calm';
}

/** "2d 06:58:24". */
export function countdown(ms: number): string {
  if (ms <= 0) return 'Closed';
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86_400);
  const hh = String(Math.floor((s % 86_400) / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${days}d ${hh}:${mm}:${ss}`;
}

/** { days: 2, clock: "06:58:24" } — the parts of the deal page's large countdown. */
export function countdownParts(ms: number): { days: number; clock: string } {
  const s = Math.max(0, Math.floor(ms / 1000));
  const hh = String(Math.floor((s % 86_400) / 3600)).padStart(2, '0');
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return { days: Math.floor(s / 86_400), clock: `${hh}:${mm}:${ss}` };
}

/** "2d 18h" — the coarse form, for perks. */
export function shortCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const hours = Math.floor(ms / HOUR);
  if (hours < 1) return `${Math.max(1, Math.floor(ms / 60_000))}m`;
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/** What a screen reader hears instead of a ticking clock. */
export function countdownLabel(ms: number): string {
  if (ms <= 0) return 'closed';
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  const parts: string[] = [];
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  return `closes in ${parts.join(', ')}`;
}

/**
 * Date parts assembled by hand: en-GB's own short form ("Sun, 27 Sept 2026")
 * is wider than a card can spare, and the comma adds nothing.
 */
function parts(date: Date, options: Intl.DateTimeFormatOptions) {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    ...options,
    hourCycle: 'h23',
  }).formatToParts(date)) {
    out[p.type] = p.value;
  }
  return out;
}

/**
 * "BST", "CEST", "EDT": en-GB knows abbreviations for European zones that
 * en-US renders as "GMT+2", so the zone name comes from its own formatter.
 */
export function zoneName(date: Date, timeZone?: string): string {
  return (
    new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'short' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * ["Sun 27 Sep 2026", "18:00 BST"], in the viewer's own time zone unless
 * another is given — two parts so a narrow card breaks between date and time,
 * never inside either.
 */
export function deadlineDate(date: Date, timeZone?: string): [string, string] {
  const p = parts(date, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return [
    `${p.weekday} ${p.day} ${p.month} ${p.year}`,
    `${p.hour}:${p.minute} ${zoneName(date, timeZone)}`,
  ];
}

function offsetIn(date: Date, timeZone: string): string {
  return (
    new Intl.DateTimeFormat('en-GB', { timeZone, timeZoneName: 'longOffset' })
      .formatToParts(date)
      .find((p) => p.type === 'timeZoneName')?.value ?? ''
  );
}

/**
 * The time zone a platform's own page states its deadlines in, read off the
 * country it is based in — but only where that country keeps one clock at
 * that moment. Germany's two zones agree, so Companisto answers Berlin; the
 * US, Canada and Australia span several, and naming one would be a guess.
 */
export function platformTimeZone(
  country: string | null,
  at: Date,
): string | null {
  const zones = country ? getCountry(country)?.timezones : undefined;
  if (!zones?.length) return null;
  try {
    const offset = offsetIn(at, zones[0]);
    return zones.every((zone) => offsetIn(at, zone) === offset)
      ? zones[0]
      : null;
  } catch {
    return null;
  }
}

/** "Sun 27 Sep, 17:00 BST" — the compact form for a second time zone. */
export function zonedShort(date: Date, timeZone?: string): string {
  const p = parts(date, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute} ${zoneName(date, timeZone)}`;
}

/** "Mon 28 Sep". */
export function shortDate(date: Date): string {
  const p = parts(date, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "Sep 2026", or "September 2026" in full. */
export function monthYear(date: Date, form: 'short' | 'long' = 'short') {
  const p = parts(date, { month: form, year: 'numeric' });
  return `${p.month} ${p.year}`;
}

/** "26 Sep", in the viewer's own time zone unless another is given. */
export function dayMonth(date: Date, timeZone?: string): string {
  const p = parts(date, { timeZone, day: 'numeric', month: 'short' });
  return `${p.day} ${p.month}`;
}

/**
 * "24 Sep 2026". A calendar date from a filing is stored as midnight UTC, so
 * it is formatted with timeZone 'UTC' — in the viewer's zone, anyone west of
 * Greenwich would read the day before.
 */
export function longDate(date: Date, timeZone?: string): string {
  const p = parts(date, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${p.day} ${p.month} ${p.year}`;
}

export function clockTime(date: Date): string {
  const p = parts(date, { hour: '2-digit', minute: '2-digit' });
  return `${p.hour}:${p.minute} ${zoneName(date)}`;
}

/** "6 min ago", "3 h ago", "2 d ago". */
export function ago(date: Date, now: number): string {
  const ms = Math.max(0, now - date.getTime());
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/**
 * "13 h ago", "3 days ago": how long since something arrived. A calendar date
 * (a filing date, stored as midnight UTC) has no time of day, so it counts in
 * whole days against the viewer's own calendar — "today", "yesterday" — and
 * never pretends to hours.
 */
export function sinceArrival(date: Date, now: number): string {
  const calendarDate =
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;
  let days: number;
  if (calendarDate) {
    const today = new Date(now);
    days = Math.round(
      (Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) -
        date.getTime()) /
        DAY,
    );
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
  } else {
    const ms = Math.max(0, now - date.getTime());
    if (ms < HOUR) return `${Math.max(1, Math.floor(ms / 60_000))} min ago`;
    if (ms < DAY) return `${Math.floor(ms / HOUR)} h ago`;
    days = Math.floor(ms / DAY);
  }
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

export const REGIME_LABELS: Record<string, string> = {
  REG_CF: 'Reg CF',
  REG_A: 'Reg A+',
  ECSPR: 'ECSPR',
  FCA: 'FCA',
  CSF: 'CSF',
  NI_45_110: 'NI 45-110',
  // Platform regimes no raise in the feed carries yet (the directory's badges).
  SC_RMO: 'SC-RMO',
  CVM: 'CVM',
  NZ_FMC: 'FMC Act',
  // Germany's national regime, which Companisto and FunderNation broker under.
  DE_34F: '§34f GewO',
  OTHER: 'Other',
};

/** Two letters for a platform chip: "StartEngine" → "SE", "Mamacrowd" → "Ma". */
const MONOGRAMS: Record<string, string> = { 'SEC EDGAR': 'SEC' };

export function monogram(name: string): string {
  if (MONOGRAMS[name]) return MONOGRAMS[name];
  const words = name
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const capitals = name.match(/[A-Z]/g);
  if (capitals && capitals.length >= 2) return capitals.slice(0, 2).join('');
  return name.slice(0, 2);
}

// The picker's module, kept light for the public pages' bundle.
export { countryFlag, countryName } from './countries';
